import React, { useMemo } from 'react';
import { Eventcalendar as MbscEventCalendar } from '@mobiscroll/react';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import {
  EventType,
  EventColors,
} from '../../../../../../../../../core/enums/event-type';
import { Announcement } from '@models/announcement';
import { useActivitiesForDay } from '../../../../../../features/activities/queries/activities.queries';
import { useSite } from '../../../../../../features/sites/queries/use-site.queries';
import { formatRecurrenceText } from '../../../../../../features/activities/utils/recurrence-utils';

type DailyAgendaProps = {
  timezone;
  activeDay;
  activeEvent: Announcement;
  selectedDate: Date;
};

// Extract siteId from URL path like /manage/content/:siteId/activities
function getSiteIdFromUrl(): number | null {
  const path = window.location.pathname;
  const match = path.match(/\/manage\/content\/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

export function DailyAgenda({
  timezone,
  activeDay,
  activeEvent,
  selectedDate,
}: DailyAgendaProps) {
  // Extract siteId from URL
  const siteId = getSiteIdFromUrl();

  // Fetch site data to get timezone if not provided
  const {
    data: site,
    isLoading: isSiteLoading,
    error: siteError,
  } = useSite(siteId);

  const effectiveTimezone = timezone || site?.timezone;

  // Fetch activities for the selected day using React Query
  const {
    data: activities = [],
    isLoading: isActivitiesLoading,
    error: activitiesError,
  } = useActivitiesForDay({
    siteId,
    selectedDate: parseISO(activeDay),
    timezone: effectiveTimezone,
  });

  const loading = isSiteLoading || isActivitiesLoading;

  // Transform activities data for Mobiscroll calendar
  const calendarEvents = useMemo(() => {
    if (!activities || !effectiveTimezone) return [];

    return activities
      .filter((activity) => {
        // Filter out invalid activities to prevent runtime errors
        if (
          !activity ||
          !activity.id ||
          !activity.title ||
          !activity.eventStart ||
          !activity.eventEnd
        ) {
          console.warn('Skipping invalid activity:', activity);
          return false;
        }
        return true;
      })
      .map((activity) => {
        try {
          // Properly convert UTC times to site's timezone
          const eventStartDate = parseISO(activity.eventStart.toString());
          const eventEndDate = parseISO(activity.eventEnd.toString());

          // Use utcToZonedTime to correctly convert from UTC to site's timezone
          const startInSiteTimezone = utcToZonedTime(
            eventStartDate,
            effectiveTimezone,
          );
          const endInSiteTimezone = utcToZonedTime(
            eventEndDate,
            effectiveTimezone,
          );

          const location = activity.location
            ? activity.location
            : activity.room?.shortName &&
              activity.room?.shortName !== ' - ' &&
              activity.room?.shortName !== '-'
            ? activity.room?.shortName
            : '';

          // Generate proper recurrence text instead of hardcoded "Recurring Event"
          const recurrenceText = activity?.recurrence
            ? formatRecurrenceText(activity.recurrence)
            : null;

          return {
            id: activity.id,
            start: startInSiteTimezone,
            end: endInSiteTimezone,
            title: activity.title,
            allDay: activity.allDay,
            color: EventColors[activity.eventType],
            eventType: activity.eventType,
            location: location,
            original: {
              ...activity,
              activeEvent: activeEvent && activity.id === activeEvent.id,
              room: {
                long: activity.room?.name,
                short: location,
              },
              recurrence: recurrenceText,
            },
          };
        } catch (error) {
          console.error(
            'Error processing activity for calendar:',
            activity,
            error,
          );
          return null;
        }
      })
      .filter(Boolean); // Remove any null entries from failed processing
  }, [activities, effectiveTimezone, activeEvent]);
  function renderHeaderTemplate() {
    return activeDay?.monthLong;
  }

  function renderAgendaListTemplate(data) {
    if (loading) {
      return (
        <div className="w-full h-full flex justify-center items-center">
          <CircularProgress color="secondary" />
        </div>
      );
    }

    if (activitiesError) {
      return (
        <div className="w-full h-full flex justify-center items-center">
          <p className="text-red-600">
            Failed to load activities. Please try again.
          </p>
        </div>
      );
    }

    return (
      <>
        {data.map((day) => (
          <ul key={day.timestamp}>
            {day.events?.map((event: any) => (
              <li key={event.id}>
                <div className="mbsc-event">
                  <div
                    className={`agenda-wrapper ${
                      event.original.activeEvent ? 'active-event' : ''
                    }`}
                  >
                    <Tooltip title={EventType[event.original.eventType]}>
                      <div
                        className="mbsc-event-type-indicator"
                        style={{ backgroundColor: event.color }}
                      ></div>
                    </Tooltip>

                    {event.allDay ? (
                      <div className="mbsc-event-time italic">
                        All Day Event
                      </div>
                    ) : (
                      <div className="mbsc-event-time">
                        {event.start} - {event.end}
                      </div>
                    )}
                    <div className="mbsc-event-content">
                      <div className="event-title">
                        {event.original.title}
                        <span className="event-location">
                          ({event.original?.original?.room?.long}
                          {event.original?.original?.room?.short
                            ? `, ${event.original?.original?.room?.short}`
                            : ''}
                          )
                        </span>
                      </div>
                    </div>
                  </div>
                  {event.original?.recurrence && (
                    <span
                      className="material-icons"
                      title={`Repeats ${event.original?.recurrence}`}
                    >
                      refresh
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ))}
      </>
    );
  }
  // Show loading state while site is loading
  if (isSiteLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <CircularProgress color="secondary" />
        <span className="ml-2">Loading site information...</span>
      </div>
    );
  }

  // Show error if site loading failed
  if (siteError || !siteId) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <p className="text-red-600">
          Failed to load site information. Please check the URL and try again.
        </p>
      </div>
    );
  }

  return (
    <>
      <MbscEventCalendar
        data={calendarEvents}
        selectedDate={activeDay}
        renderHeader={renderHeaderTemplate}
        renderAgenda={renderAgendaListTemplate}
        view={{ agenda: { type: 'day' } }}
        dateFormatLong="DDDD, MMM D, YYYY"
        noEventsText="No scheduled events"
      />
      {/* <mbsc-eventcalendar
  [data]="events.value"
  [options]="options"
  [selectedDate]="activeDay"
  [headerTemplate]="customHeader"
  [agendaTemplate]="agendaList"
>
  <ng-template #customHeader>
    {{ activeDay?.monthLong }}
  </ng-template>
  <ng-template #agendaList let-data>
    <div class="agenda-content-wrapper">
      <ng-container *ngIf="loading">
        <mat-spinner diameter="60" color="accent" class="loader"></mat-spinner>
      </ng-container>

      <ng-container *ngIf="!loading">
        <ng-container>
          <ul
            *ngFor="let day of data"
            mbsc-event-list-day
            [timestamp]="day.timestamp"
          >
            <li *ngFor="let event of day.events">
              <div class="mbsc-event">
                <div
                  [ngClass]="{
                    'agenda-wrapper': true,
                    'active-event': event.original.activeEvent
                  }"
                >
                  <div
                    [matTooltip]="eventTypes[event.original.eventType]"
                    class="mbsc-event-type-indicator"
                    [style.backgroundColor]="event.color"
                  ></div>
                  <div
                    class="mbsc-event-time"
                    *ngIf="!event.allDay; else allDay"
                  >
                    {{ event.start }} - {{ event.end }}
                  </div>
                  <ng-template #allDay>
                    <div class="mbsc-event-time italic">All Day Event</div>
                  </ng-template>
                  <div class="mbsc-event-content">
                    <div class="event-title">
                      {{ event.original.title }}
                      <span class="event-location"
                        >({{ event.original.room.long
                        }}{{
                          event.original.room.short
                            ? ', ' + event.original.room.short
                            : ''
                        }})</span
                      >
                    </div>
                  </div>
                </div>

                <ng-container *ngIf="event.original?.recurrence">
                  <span
                    class="material-icons"
                    [matTooltip]="'Repeats ' + event.original?.recurrence"
                  >
                    refresh
                  </span>
                </ng-container>
              </div>
            </li>
          </ul>
        </ng-container>
        <ng-container *ngIf="events.value.length === 0">
          No Events Scheduled This Day
        </ng-container>
      </ng-container>
    </div>
  </ng-template>
</mbsc-eventcalendar> */}
    </>
  );
}
